import { api, normalizeDomain, normalizeUrl } from './api';
import type { Ads, AnalyzeSite, IndependentSearch, Metrics, Positioning, ProductSummary, Social, Strategy } from './schemas';

export type ModuleStatus='pending'|'running'|'success'|'partial'|'error';
export interface ModuleResult{status:ModuleStatus;error?:string}
export interface AnalysisResult{
  runId:string;domain:string;url:string;startedAt:string;completedAt:string;website:AnalyzeSite;catalog:ProductSummary|null;
  metrics:Metrics|null;positioning:Positioning['positioning']|null;social:Social|null;ads:Ads|null;search:IndependentSearch|null;strategy:Strategy|null;history:unknown|null;
  snapshotObservedAt:string|null;modules:Record<string,ModuleResult>;runStatus:'success'|'partial';
}
function moduleFrom<T>(r:PromiseSettledResult<T>):ModuleResult{
  if(r.status==='fulfilled')return{status:'success'};
  return{status:'error',error:r.reason instanceof Error?r.reason.message:String(r.reason)};
}
function isCatalogVerified(platform:string|null|undefined,p:ProductSummary|null){
  if(!p)return false;
  // Partial/mid-pagination catalogs are observations, not confirmed totals.
  // Do not turn a Shopify first page or 2000-item truncation into a full count.
  return p.catalogComplete===true && p.count>0;
}
function summarizeStatus(modules:Record<string,ModuleResult>):'success'|'partial'{
  return Object.values(modules).some(x=>x.status==='error'||x.status==='partial')?'partial':'success';
}

export async function runAnalysisPipeline(rawDomain:string,signal:AbortSignal):Promise<AnalysisResult>{
  const domain=normalizeDomain(rawDomain),url=normalizeUrl(rawDomain),runId=crypto.randomUUID(),startedAt=new Date().toISOString();
  const modules:Record<string,ModuleResult>={
    website:{status:'running'},catalog:{status:'pending'},search:{status:'pending'},metrics:{status:'pending'},positioning:{status:'pending'},
    social:{status:'pending'},ads:{status:'pending'},strategy:{status:'pending'},snapshot:{status:'pending'}
  };
  let tracked=false;
  const recoverLimited=async(reason:string,sourceWebsite:AnalyzeSite|null):Promise<AnalysisResult>=>{
    if(signal.aborted)throw new DOMException('Aborted','AbortError');
    modules.website={status:'partial',error:reason};
    modules.catalog={status:'running'};
    // Independent catalog and existing history are safe to try even if the
    // homepage is currently restricted. Neither invents revenue or followers.
    // These independent public sources do not contact the merchant homepage.
    const [catalogR,historyR,searchR,adsR,socialR]=await Promise.allSettled([
      api.catalog(url,signal),api.history(domain,signal),
      api.search(url,signal),api.ads(url,signal),api.social(url,signal)
    ]);
    if(signal.aborted)throw new DOMException('Aborted','AbortError');
    const catalog=catalogR.status==='fulfilled'?catalogR.value.products:null;
    const fromCache=catalogR.status==='fulfilled'&&catalogR.value.cached===true;
    modules.catalog=catalogR.status==='fulfilled'
      ?fromCache?{status:'partial',error:'DATED_CACHED_CATALOG: '+(catalogR.value.cacheObservedAt||catalogR.value.observedAt||'unknown')}
       :isCatalogVerified('Shopify',catalog)?{status:'success'}
       :{status:'partial',error:'CATALOG_INCOMPLETE'}
      :{status:'partial',error:'CATALOG_ACCESS_RESTRICTED: '+(catalogR.reason instanceof Error?catalogR.reason.message:String(catalogR.reason))};
    const search=searchR.status==='fulfilled'?searchR.value:null;
    const ads=adsR.status==='fulfilled'?adsR.value:null;
    const social=socialR.status==='fulfilled'?socialR.value:null;
    modules.search=searchR.status==='fulfilled'&&searchR.value.status==='observed'
      ?{status:'success'}:{status:'partial',error:searchR.status==='rejected'?String(searchR.reason):'SEARCH_PROVIDER_UNAVAILABLE'};
    modules.ads=adsR.status==='fulfilled'&&ads?.ads?.googleSearchAds?.connected===true
      ?{status:'success'}:{status:'partial',error:adsR.status==='rejected'?String(adsR.reason):'AD_PROVIDER_NOT_CONNECTED'};
    modules.social=socialR.status==='fulfilled'&&social?.profiles?.some(p=>p.followers!=null||p.recentPosts.length>0)
      ?{status:'success'}:{status:'partial',error:socialR.status==='rejected'?String(socialR.reason):'ONLY_HISTORICAL_SOCIAL_LINKS_OR_NO_PUBLIC_STATS'};
    for(const name of ['metrics','positioning','strategy','snapshot']){
      modules[name]={status:'partial',error:'NOT_COLLECTED: reliable website and catalog inputs unavailable'};
    }
    const history=historyR.status==='fulfilled'?historyR.value:null;
    if(tracked&&!signal.aborted){
      try{await api.analysisRunUpdate(runId,'partial',modules,signal)}catch(e){console.warn('analysis run tracking update failed',e)}
    }
    const limitedWebsite:AnalyzeSite=sourceWebsite||{
      ok:true,observedAt:startedAt,
      website:{domain,finalUrl:url,title:domain},
      technology:{platform:catalog?'Shopify':'Unknown'},
      social:{},
      limitations:['Homepage restricted. Independently collected public catalog and dated historical observations are reported separately.'],
      provenance:{classification:'unavailable',provider:'WebsiteProvider'}
    };
    return {runId,domain,url,startedAt,completedAt:new Date().toISOString(),
      website:limitedWebsite,catalog,metrics:null,positioning:null,social,ads,search,strategy:null,
      history,snapshotObservedAt:null,modules,runStatus:'partial'};
  };
  try{
    try{await api.analysisRunStart(runId,domain,signal);tracked=true}catch(e){console.warn('analysis run tracking start failed',e)}
    let website:AnalyzeSite;
    try {
      website=await api.analyzeSite(url,signal);
      modules.website=website.partial===true?{status:'partial',error:'HOMEPAGE_RESTRICTED: Shopify public catalog only'}:{status:'success'};
    } catch (error) {
      if(signal.aborted)throw error;
      const reason=error instanceof Error?error.message:String(error);
      // A storefront can throttle anonymous requests. Keep the analysis readable,
      // but never invent a website, catalog, traffic or revenue observation.
      if(!/UPSTREAM_HTTP_(?:403|429|503)|UPSTREAM_RATE_LIMITED|HTTP_429|HTTP_403|PROVIDER_TIMEOUT|SOURCE_COOLDOWN/.test(reason))throw error;
      return recoverLimited('SOURCE_ACCESS_RESTRICTED: '+reason,null);
    }
    if(website.partial===true || website.cached===true){
      return recoverLimited('CACHED_OR_PARTIAL_HOMEPAGE: '+(website.observedAt||'unverified'),website);
    }
    const finalUrl=website.website.finalUrl||url;
    modules.catalog={status:'running'};modules.search={status:'running'};modules.metrics={status:'running'};modules.positioning={status:'running'};modules.social={status:'running'};modules.ads={status:'running'};

    const [catalogR,metricsR,positioningR,socialR,adsR,searchR]=await Promise.allSettled([
      api.catalog(finalUrl,signal),api.metrics(finalUrl,signal,runId),api.positioning(finalUrl,signal),api.social(finalUrl,signal),api.ads(finalUrl,signal),api.search(finalUrl,signal)
    ]);
    modules.search=searchR.status==='fulfilled'&&searchR.value.status==='observed'?{status:'success'}:{status:'partial',error:searchR.status==='rejected'?String(searchR.reason):'SEARCH_PROVIDER_UNAVAILABLE'};
    modules.catalog=catalogR.status==='fulfilled'&&catalogR.value.cached===true?{status:'partial',error:'DATED_CACHED_CATALOG'}:moduleFrom(catalogR);modules.metrics=moduleFrom(metricsR);modules.positioning=moduleFrom(positioningR);modules.social=moduleFrom(socialR);modules.ads=moduleFrom(adsR);

    const catalog=catalogR.status==='fulfilled'?catalogR.value.products:metricsR.status==='fulfilled'&&metricsR.value.catalog?metricsR.value.catalog:null;
    let strategy:Strategy|null=null;
    modules.strategy={status:'running'};
    if(socialR.status==='fulfilled'){
      try{const x=await api.strategy(finalUrl,signal);strategy=x;modules.strategy={status:x.status==='insufficient_post_data'?'partial':'success'};}
      catch(e){modules.strategy={status:'error',error:e instanceof Error?e.message:String(e)}}
    }else modules.strategy={status:'partial',error:'SOCIAL_DATA_UNAVAILABLE'};

    let snapshotObservedAt:string|null=null;
    modules.snapshot={status:'running'};
    if(modules.website.status==='success'&&catalogR.status==='fulfilled'&&catalogR.value.cached!==true&&isCatalogVerified(website.technology?.platform,catalog)){
      try{
        const x=await api.saveSnapshot({
          url:finalUrl,modelVersion:'v2.1_xstate',analysisRunId:runId,
          snapshot:{
            brand:{name:website.website.title||domain,domain},website:website.website,technology:website.technology||null,products:catalog,
            social:website.social||null,socialData:socialR.status==='fulfilled'?socialR.value:null,adsData:adsR.status==='fulfilled'?adsR.value:null,
            metricsData:metricsR.status==='fulfilled'?metricsR.value:null,
            positioning:positioningR.status==='fulfilled'?positioningR.value.positioning||null:null,analysisRun:{id:runId,status:'complete'}
          }
        },signal);
        snapshotObservedAt=x.observedAt||new Date().toISOString();modules.snapshot={status:'success'};
      }catch(e){modules.snapshot={status:'error',error:e instanceof Error?e.message:String(e)}}
    }else modules.snapshot={status:'partial',error:modules.website.status==='partial'?'HOMEPAGE_NOT_VERIFIED':'CATALOG_NOT_VERIFIED'};

    let history:unknown|null=null;try{history=await api.history(domain,signal)}catch{}
    const runStatus=summarizeStatus(modules);
    if(tracked&&!signal.aborted){
      try{await api.analysisRunUpdate(runId,runStatus,modules,signal)}catch(e){console.warn('analysis run tracking update failed',e)}
    }
    return{
      runId,domain,url:finalUrl,startedAt,completedAt:new Date().toISOString(),website,catalog,
      metrics:metricsR.status==='fulfilled'?metricsR.value:null,
      positioning:positioningR.status==='fulfilled'?positioningR.value.positioning||null:null,
      social:socialR.status==='fulfilled'?socialR.value:null,ads:adsR.status==='fulfilled'?adsR.value:null,search:searchR.status==='fulfilled'?searchR.value:null,strategy,history,snapshotObservedAt,modules,runStatus
    };
  }catch(error){
    if(tracked&&!signal.aborted){
      const failureModules={...modules,website:modules.website.status==='running'?{status:'error' as const,error:error instanceof Error?error.message:String(error)}:modules.website};
      try{
        const ctl=new AbortController();
        await api.analysisRunUpdate(runId,'error',failureModules,ctl.signal,error instanceof Error?error.message:String(error));
      }catch{}
    }
    throw error;
  }
}
